import { useCallback, useEffect, useRef, useState } from 'react';
import { backendApi } from './api';

/**
 * How many Creator submissions wait for review — the number on the Creator
 * Approvals sidebar item. Read from the queue itself (GET
 * /api/admin/creators/submissions?status=pending&limit=1 → `total`): the same
 * filter as the Pending tab, no search, one row at most, so neither paging
 * nor the page's own filters can change it. Gated on the 'creators' module
 * here and, regardless, on the backend.
 *
 * Refreshed: when the portal opens, on each navigation, on coming back to the
 * browser tab, after a decision on the Creator Approvals page
 * (refreshCreatorPendingCount), and every POLL_MS while the tab is visible —
 * so new submissions show up without any push channel.
 */

export const POLL_MS = 60_000;
/** Focus, a tab switch and a navigation can arrive together: one request for them. */
const MIN_GAP_MS = 2_000;
const EVENT = 'wpos:creator-pending-changed';

/** Ask every badge to read the count again (after an approve or reject). */
export function refreshCreatorPendingCount(): void {
  window.dispatchEvent(new Event(EVENT));
}

/** "5", "99+", or '' for none — the badge is shown only when there is something to say. */
export function formatPendingCount(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return '';
  return n > 99 ? '99+' : String(Math.floor(n));
}

/**
 * The pending count, or null while unknown (not loaded yet, not allowed, or
 * the backend unreachable — a failed refresh keeps the last good number).
 * `navKey` changes on every navigation (the current path).
 */
export function useCreatorPendingCount(enabled: boolean, navKey?: string): number | null {
  const [count, setCount] = useState<number | null>(null);
  const last = useRef(0);
  const inFlight = useRef(false);
  /** A decision landed while a read was under way: read once more after it. */
  const again = useRef(false);

  const load = useCallback((force = false) => {
    if (!enabled) return;
    if (inFlight.current) { if (force) again.current = true; return; }
    const now = Date.now();
    if (!force && now - last.current < MIN_GAP_MS) return;
    last.current = now;
    inFlight.current = true;
    backendApi.get<{ total: number }>('/admin/creators/submissions?status=pending&limit=1')
      .then((d) => setCount(typeof d?.total === 'number' ? d.total : null))
      .catch((err: { status?: number }) => {
        // No access (any more): no number. Anything else: keep what we had.
        if (err?.status === 401 || err?.status === 403) setCount(null);
      })
      .finally(() => {
        inFlight.current = false;
        if (again.current) { again.current = false; load(true); }
      });
  }, [enabled]);

  // Opening the portal, and each navigation (back to the page included).
  useEffect(() => {
    if (!enabled) { setCount(null); return; }
    load();
  }, [enabled, navKey, load]);

  useEffect(() => {
    if (!enabled) return;
    const onDecision = () => load(true);
    const onReturn = () => { if (document.visibilityState === 'visible') load(); };
    const timer = window.setInterval(onReturn, POLL_MS);
    window.addEventListener(EVENT, onDecision);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(EVENT, onDecision);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [enabled, load]);

  return count;
}
